import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "steptember-install-dismissed";

interface InstallContextValue {
  /** The app is not already running as an installed window. */
  canInstall: boolean;
  /** A deferred `beforeinstallprompt` is in hand, so we can install natively. */
  canPromptNatively: boolean;
  /** The one-time banner should be on screen. */
  showBanner: boolean;
  /** Hide the banner for good; the nav entry point stays available. */
  dismissBanner: () => void;
  /** The manual how-to-install dialog is open. */
  showHelp: boolean;
  closeHelp: () => void;
  install: () => Promise<void>;
}

const InstallContext = createContext<InstallContextValue>({
  canInstall: false,
  canPromptNatively: false,
  showBanner: false,
  dismissBanner: () => {},
  showHelp: false,
  closeHelp: () => {},
  install: async () => {},
});

export function useInstall() {
  return useContext(InstallContext);
}

/** Running inside an installed window rather than a browser tab. */
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: window-controls-overlay)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    // iOS Safari predates the display-mode media query.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * Owns the `beforeinstallprompt` event for the whole app so the banner and the
 * navigation entry point share one deferred prompt.
 *
 * The entry points deliberately do NOT depend on that event. Chromium only
 * fires it once engagement heuristics pass, never re-fires it while the app is
 * already installed, and Edge/Safari/Firefox often skip it entirely — gating
 * the nav row on it made the install action silently disappear. The row is
 * offered until the app is genuinely running standalone, falling back to a
 * short how-to dialog when no native prompt is available.
 */
export function InstallProvider({ children }: { children: ReactNode }) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(true);
  const [installed, setInstalled] = useState(true);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const standalone = isStandalone();
    setInstalled(standalone);
    if (standalone) return;

    setDismissed(localStorage.getItem(DISMISSED_KEY) === "1");

    function onPrompt(event: Event) {
      // Keep the event so the install can happen from our own UI instead of
      // whatever mini-infobar the browser would have shown.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    }

    function onInstalled() {
      setDeferred(null);
      setInstalled(true);
      localStorage.setItem(DISMISSED_KEY, "1");
      setDismissed(true);
    }

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismissBanner = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  }, []);

  const closeHelp = useCallback(() => setShowHelp(false), []);

  const install = useCallback(async () => {
    if (!deferred) {
      // No native prompt on offer — show where the browser keeps the action.
      setShowHelp(true);
      return;
    }
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // A deferred prompt can only be used once; Chromium re-fires the event if
    // the user backs out, which restores the entry point.
    setDeferred(null);
    if (outcome === "accepted") {
      localStorage.setItem(DISMISSED_KEY, "1");
      setDismissed(true);
    }
  }, [deferred]);

  const value = useMemo(
    () => ({
      canInstall: !installed,
      canPromptNatively: deferred !== null,
      showBanner: deferred !== null && !dismissed && !installed,
      dismissBanner,
      showHelp,
      closeHelp,
      install,
    }),
    [installed, deferred, dismissed, dismissBanner, showHelp, closeHelp, install],
  );

  return (
    <InstallContext.Provider value={value}>
      {children}
    </InstallContext.Provider>
  );
}
